import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { GoogleMark } from "@/components/GoogleMark";

const field = "w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resendLink, setResendLink] = useState("");
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("expired")) toast.info("Your session ended — please sign in again");
  }, [params]);

  const route = (user) => {
    if (user.role === "admin") navigate("/admin");
    else if (user.role === "guardian") navigate("/guardian");
    else navigate(user.profile_complete ? "/dashboard" : "/onboarding");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      signIn(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      route(data.user);
    } catch (err) {
      const detail = errMsg(err);
      setNeedsVerify(err?.response?.status === 403 && detail.toLowerCase().includes("verify"));
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      setResendLink(data.verification_link || "");
      toast.success(data.verification_link ? "New verification link created below" : data.message);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4 py-12">
      <div className="relative z-10 w-full max-w-md reveal">
        <Logo size="md" testId="login-logo" />
        <h1 className="mt-8 text-3xl font-extrabold">Sign in</h1>
        <p className="mt-2 text-sm text-slate-500">Students and guardians use the same door.</p>


        <form onSubmit={submit} className="mt-8 card-flat p-7 space-y-4">
          <div>
            <label className="rule-label">Email</label>
            <input data-testid="auth-email" className={`${field} mt-2`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="rule-label">Password</label>
              <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-xs font-semibold text-blue-700 hover:underline">
                Forgot password?
              </Link>
            </div>
            <input data-testid="auth-password" className={`${field} mt-2`} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {needsVerify && (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="auth-verify-hint">
              <p className="text-sm text-amber-900">Your email isn't verified yet.</p>
              <button type="button" data-testid="auth-resend-verification" onClick={resend} className="mt-2 text-sm font-semibold text-amber-900 underline">
                Send the verification link again
              </button>
              {resendLink && (
                <Link to={resendLink} data-testid="auth-resend-link" className="mt-2 block break-all text-xs font-semibold text-blue-700 hover:underline">
                  {resendLink}
                </Link>
              )}
            </div>
          )}
          <button data-testid="auth-login-submit" disabled={busy} className="w-full py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <button type="button" data-testid="auth-google" onClick={google} className="w-full py-3 rounded-xl border border-slate-300 font-semibold text-slate-700 hover:border-slate-500 hover:bg-slate-50 transition-colors">
            <span className="inline-flex items-center justify-center gap-2.5">
              <GoogleMark />
              Continue with Google
            </span>
          </button>
        </form>

        <p className="mt-6 text-sm text-slate-500">
          New here?{" "}
          <Link to="/signup" data-testid="auth-goto-signup" className="font-semibold text-blue-700 hover:underline">
            Create a student account
          </Link>
        </p>
      </div>
    </div>
  );
}
